import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { createPortal } from "react-dom";
import type { GroupInfo, MaterialKind } from "../../../shared/api-contract";
import { useViewer } from "../../../state/ViewerProvider";
import { notice, useDomainState } from "../../../state/editor/useEditor";
import { loadVerdict, modelSetKey, settleLoad } from "../../../state/editor/modelLoads";
import { Icon } from "../../kit";
import { partName } from "../basic/rail/usePartScope";
import { useBasicEngine } from "../basic/useBasicEngine";
import { defaultMapping, defaultPreset, type MatcherCatalog } from "./materialMatcher";

/*
 * "Apply default materials" — asked once per NEW piece.
 *
 * When a model arrives on its own (a dropped or uploaded file, a Vault model
 * with no saved scene, /dev/studio?model=…), every part gets a row:
 *
 *   - parts named like iJewel's default layers (Metal, Metal 01 … Gem 09) are
 *     MATCHED to white gold, polished / a clear diamond;
 *   - every other part is pre-filled with the word matcher's best guess
 *     ("18K_Rose" → rose gold) or the viewer's own metal-or-stone call, and
 *     marked "Choose" until the user touches it.
 *
 * A model that comes WITH a saved scene is never asked about — the scene
 * already says what each part is made of. That is decided by the load ledger
 * (`state/editor/modelLoads.ts`), which the scene restores write to before the
 * model is added, so the dialog cannot flash and then vanish.
 *
 * Apply is ONE Undo step (`applySuggestions`). Either answer is remembered per
 * piece — keyed by its group ids, which are derived from the file's content
 * and stable across loads (contract v47), unlike model ids — so loading the
 * same file again asks again, pre-filled with what was chosen last time.
 *
 * This replaced the materials check (a card that spoke only when a part's name
 * disagreed with its look, so a file with generic names got nothing), which in
 * turn replaced the old "Apply default materials?" toast that counted parts
 * rather than matching any name and painted everything yellow gold.
 */

const ANSWERS_KEY = "studio.defaultMaterials.answers";
/** The materials check's record: which pieces were answered, without the answers. Superseded. */
const OLD_HANDLED_KEY = "studio.materialsCheck.handled";
const MAX_REMEMBERED = 200;

interface Answer {
  kind: MaterialKind;
  metal: string;
  finish: string;
  stone: string;
  /** The user changed this row (rather than accepting the proposal). */
  touched: boolean;
}

interface Row extends Answer {
  group: GroupInfo;
  /** The file's name for the part, tidied. The raw name is the tooltip. */
  name: string;
  /** Named like a default layer (Metal 01, Gem 01 …). */
  matched: boolean;
  /** The proposal came from a word in the name, not just the viewer's metal-or-stone call. */
  guessed: boolean;
}

// ── remembered answers ────────────────────────────────────────────────────

/** Short, stable id for a piece: a 53-bit string hash of its sorted group ids. */
function pieceKey(sortedGroupIds: readonly string[]): string {
  const s = sortedGroupIds.join("|");
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 2654435761);
    h2 = Math.imul(h2 ^ c, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

/** One stored piece: answers in the order of its sorted group ids, so the long ids are not repeated. */
interface Stored {
  k: string;
  a: (Answer | null)[];
}

function readStore(): Stored[] {
  try {
    const raw = localStorage.getItem(ANSWERS_KEY);
    const v = raw ? JSON.parse(raw) : [];
    return Array.isArray(v) ? v.filter((e): e is Stored => !!e && typeof e.k === "string" && Array.isArray(e.a)) : [];
  } catch {
    return [];
  }
}

function isAnswer(v: unknown): v is Answer {
  const a = v as Answer | null;
  return (
    !!a &&
    (a.kind === "metal" || a.kind === "gem") &&
    typeof a.metal === "string" &&
    typeof a.finish === "string" &&
    typeof a.stone === "string" &&
    typeof a.touched === "boolean"
  );
}

function recall(sortedIds: readonly string[]): Map<string, Answer> {
  const hit = readStore().find((e) => e.k === pieceKey(sortedIds));
  const out = new Map<string, Answer>();
  if (!hit || hit.a.length !== sortedIds.length) return out;
  sortedIds.forEach((id, i) => {
    const a = hit.a[i];
    if (isAnswer(a)) out.set(id, a);
  });
  return out;
}

function remember(sortedIds: readonly string[], rows: readonly Row[]) {
  const byId = new Map(rows.map((r) => [r.group.id, r]));
  const k = pieceKey(sortedIds);
  const entry: Stored = {
    k,
    a: sortedIds.map((id) => {
      const r = byId.get(id);
      return r ? { kind: r.kind, metal: r.metal, finish: r.finish, stone: r.stone, touched: r.touched } : null;
    }),
  };
  try {
    localStorage.setItem(ANSWERS_KEY, JSON.stringify([entry, ...readStore().filter((e) => e.k !== k)].slice(0, MAX_REMEMBERED)));
    localStorage.removeItem(OLD_HANDLED_KEY);
  } catch {
    /* private mode or full: next time simply starts from the guesses */
  }
}

// ── rows ──────────────────────────────────────────────────────────────────

function initialRows(groups: readonly GroupInfo[], cat: MatcherCatalog, remembered: Map<string, Answer>): Row[] {
  const proposals = defaultMapping(
    // ONLY the file's own name. `materialLabel` is the viewer's label for the
    // material IT assigned ("whiteGold polished"), not anything the file said.
    groups.map((g) => ({ id: g.id, label: g.label, kind: g.kind })),
    cat,
  );
  const metal = defaultPreset("metal", cat);
  const stone = defaultPreset("gem", cat);
  const seen: Record<MaterialKind, number> = { metal: 0, gem: 0 };
  return groups.map((g, i) => {
    const p = proposals[i];
    const row: Row = {
      group: g,
      name: partName(g, seen[g.kind]++, g.kind),
      matched: p.matched,
      guessed: !p.matched && !!p.because,
      kind: p.kind,
      metal: p.kind === "metal" ? p.presetId : metal.presetId,
      finish: p.finishId ?? metal.finishId ?? "polished",
      stone: p.kind === "gem" ? p.presetId : stone.presetId,
      touched: false,
    };
    const a = remembered.get(g.id);
    // A default layer name (Metal / Gem 01–09) says what the part IS. An answer
    // remembered from a load where it was switched to the other kind must not
    // win over it — that is how "Gem 01" kept coming back as metal.
    if (!a || (p.matched && a.kind !== p.kind)) return row;
    // Last time's answer, as far as this viewer can still render it.
    return {
      ...row,
      kind: a.kind,
      metal: cat.metalIds.includes(a.metal) ? a.metal : row.metal,
      finish: cat.finishIds.includes(a.finish) ? a.finish : row.finish,
      stone: cat.gemIds.includes(a.stone) ? a.stone : row.stone,
      touched: a.touched,
    };
  });
}

/** Stones in a jeweller's words. `diamondClear2` renders the same as `diamondClear`, so only one is offered. */
const STONE_WORDS: Record<string, string> = {
  diamondClear: "Diamond",
  diamondWarm: "Warm diamond",
  ruby: "Ruby",
  sapphire: "Sapphire",
  emerald: "Emerald",
};
const FINISH_WORDS: Record<string, string> = {
  hammered: "Hammered",
  brushed: "Brushed",
  linear: "Line brushed",
  sand: "Sandblast",
  ice: "Ice",
  nature: "Nature",
};

type Option = { id: string; label: string };

// ── the gate: decides WHETHER to ask ──────────────────────────────────────

/**
 * Mounted with the studio chrome. Watches the set of models on screen and
 * opens the dialog for a load the ledger calls "ask".
 */
export function DefaultMaterials() {
  const { client } = useViewer();
  const models = useDomainState("models");
  const loadKey = modelSetKey(models.map((m) => m.id));
  // The verdict reads each model's `embeddedScene` (v65: a file that brought
  // its own scene is never asked about) — from the render the key changed in.
  const modelsRef = useRef(models);
  modelsRef.current = models;
  const [pending, setPending] = useState<{ loadKey: string; groups: GroupInfo[] } | null>(null);

  useEffect(() => {
    if (!loadKey) return;
    const params = new URLSearchParams(window.location.search);
    // A ?config= manifest owns the material assignments (dev studio flow).
    if (params.has("config")) return;
    const modelIds = loadKey.split("|");
    if (loadVerdict(client, modelsRef.current, { bootScene: params.has("vjson") }) !== "ask") return;
    let alive = true;
    // Read the parts from the viewer, not from the store's `groups` slice:
    // the slice is refreshed by a SEPARATE call fired on the same event, so
    // right now it can still hold the previous piece. The viewer rebuilds its
    // groups before it announces a model, so this call, sent after the
    // announcement, sees the new ones.
    client.ring.listGroups().then(
      (groups) => {
        if (!alive) return;
        if (groups.length === 0) settleLoad(client, modelIds);
        else setPending({ loadKey, groups });
      },
      () => {
        /* viewer gone: nothing to ask about */
      },
    );
    return () => {
      alive = false;
    };
  }, [client, loadKey]);

  if (!pending || pending.loadKey !== loadKey) return null;
  return (
    <MaterialsDialog
      groups={pending.groups}
      onDone={() => {
        settleLoad(client, pending.loadKey.split("|"));
        setPending(null);
      }}
    />
  );
}

// ── the dialog ────────────────────────────────────────────────────────────

function MaterialsDialog({ groups, onDone }: { groups: GroupInfo[]; onDone: () => void }) {
  const { catalogs } = useViewer();
  const engine = useBasicEngine();
  const cat: MatcherCatalog = useMemo(
    () => ({
      metalIds: catalogs.metalPresets.map((p) => p.id),
      gemIds: catalogs.gemPresets.map((p) => p.id),
      finishIds: catalogs.metalFinishes.map((f) => f.id),
    }),
    [catalogs],
  );
  const sortedIds = useMemo(() => groups.map((g) => g.id).sort(), [groups]);
  const [rows, setRows] = useState<Row[]>(() => initialRows(groups, cat, recall(sortedIds)));
  const [busy, setBusy] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const applyRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const descId = useId();

  const metalOptions: Option[] = useMemo(() => catalogs.metalPresets.map((p) => ({ id: p.id, label: p.label })), [catalogs]);
  const stoneOptions: Option[] = useMemo(() => {
    const hasClear = catalogs.gemPresets.some((g) => g.id === "diamondClear");
    return catalogs.gemPresets
      .filter((g) => !(hasClear && g.id === "diamondClear2"))
      .map((g) => ({ id: g.id, label: STONE_WORDS[g.id] ?? g.label }));
  }, [catalogs]);

  const keep = () => {
    if (busy) return;
    remember(sortedIds, rows);
    onDone();
  };

  const apply = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await engine.applySuggestions(
        rows.map((r) => ({
          groupId: r.group.id,
          kind: r.kind,
          changeKind: r.kind !== r.group.kind,
          mark: r.kind === "metal" ? { presetId: r.metal, finishId: r.finish } : { presetId: r.stone },
        })),
        "Default materials",
      );
      remember(sortedIds, rows);
      onDone();
    } catch (e) {
      notice(`Couldn't apply the materials: ${(e as Error).message}`);
      setBusy(false);
    }
  };

  const update = (index: number, patch: Partial<Answer>) =>
    setRows((rs) => rs.map((r, i) => (i === index ? { ...r, ...patch, touched: true } : r)));

  // Changing a part's kind moves its row to the other section. Put focus back
  // on the switch the user just used, wherever the row now sits.
  const [refocus, setRefocus] = useState<{ index: number; kind: MaterialKind } | null>(null);
  useLayoutEffect(() => {
    if (!refocus) return;
    dialogRef.current?.querySelector<HTMLElement>(`[data-row-index="${refocus.index}"] [data-kind="${refocus.kind}"]`)?.focus();
    setRefocus(null);
  }, [refocus]);
  const setKind = (index: number, kind: MaterialKind) => {
    update(index, { kind });
    setRefocus({ index, kind });
  };

  // Focus starts on Apply and goes back where it was when the dialog closes.
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    applyRef.current?.focus();
    return () => {
      if (before?.isConnected) before.focus();
    };
  }, []);

  // Keyboard, in the capture phase so it wins over the studio's own keys:
  // Esc keeps the file's materials, Tab stays inside, and Undo / ⌘K / "/"
  // stand down while the dialog is asking.
  const keepRef = useRef(keep);
  useEffect(() => {
    keepRef.current = keep;
  });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const box = dialogRef.current;
      if (!box) return;
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        keepRef.current();
        return;
      }
      if (e.key === "Tab") {
        const stops = Array.from(box.querySelectorAll<HTMLElement>("button, select")).filter((el) => !(el as HTMLButtonElement).disabled && el.tabIndex >= 0);
        if (stops.length === 0) return;
        const first = stops[0];
        const last = stops[stops.length - 1];
        const inside = box.contains(document.activeElement);
        if (!inside || (e.shiftKey && document.activeElement === first)) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
        return;
      }
      const k = e.key.toLowerCase();
      if (((e.metaKey || e.ctrlKey) && (k === "z" || k === "y" || k === "k")) || (e.key === "/" && !e.metaKey && !e.ctrlKey)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  const toChoose = rows.filter((r) => !r.matched && !r.touched).length;
  const summary =
    toChoose === 0
      ? "Every part has a material. Apply, or change any of them first."
      : `Check the ${toChoose === 1 ? "part" : `${toChoose} parts`} marked Choose. Each is filled in with our best guess.`;

  // One flat, keyed list — section headings and rows as siblings — so a row
  // that changes kind MOVES to the other section instead of being rebuilt.
  const indexed = rows.map((row, index) => ({ row, index }));
  const metalRows = indexed.filter((x) => x.row.kind === "metal");
  const stoneRows = indexed.filter((x) => x.row.kind === "gem");
  const items: Array<{ heading: string; count: number } | { row: Row; index: number }> = [
    ...(metalRows.length ? [{ heading: "Metal parts", count: metalRows.length }] : []),
    ...metalRows,
    ...(stoneRows.length ? [{ heading: "Stone parts", count: stoneRows.length }] : []),
    ...stoneRows,
  ];

  return createPortal(
    // Above the studio chrome (z-[99]) and Present's overlay, below toasts
    // (z-[200]) so an error from Apply is still readable. Portalled to <body>:
    // the canvas card and the viewer iframe are never moved or resized.
    <div className="editor-theme fixed inset-0 z-[150] flex items-center justify-center bg-black/25 p-4" data-materials-check>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className="flex max-h-[min(680px,calc(100vh-32px))] w-[600px] max-w-full flex-col overflow-hidden rounded-xl border border-ed-border bg-ed-panel shadow-[0_24px_60px_rgba(0,0,0,0.18)]"
      >
        <header className="shrink-0 border-b border-ed-border px-5 pb-3.5 pt-4">
          <h2 id={titleId} className="text-[16px] font-semibold leading-5 text-ed-text">
            Apply default materials
          </h2>
          <p id={descId} className="mt-1 text-[13px] leading-[18px] text-ed-text-2">
            {summary}
          </p>
        </header>

        <div className="scroll-thin min-h-0 flex-1 overflow-y-auto pb-2">
          {items.map((item) =>
            "heading" in item ? (
              <h3 key={`heading:${item.heading}`} className="px-5 pb-1 pt-3.5 text-[12px] font-semibold text-ed-text-2">
                {item.heading} <span className="font-normal text-ed-text-3">· {item.count}</span>
              </h3>
            ) : (
              <PartRow
                key={item.row.group.id}
                row={item.row}
                index={item.index}
                busy={busy}
                metalOptions={metalOptions}
                stoneOptions={stoneOptions}
                onKind={(kind) => setKind(item.index, kind)}
                onPreset={(patch) => update(item.index, patch)}
              />
            ),
          )}
        </div>

        <footer className="flex shrink-0 items-center gap-2 border-t border-ed-border px-5 py-3">
          <span className="mr-auto text-[12px] text-ed-text-3 max-sm:hidden">You can change any part later.</span>
          <button
            type="button"
            onClick={keep}
            disabled={busy}
            className="h-[34px] rounded-lg border border-ed-border bg-ed-panel px-3.5 text-[13px] font-medium text-ed-text hover:bg-ed-hover disabled:opacity-50"
            data-materials-keep
          >
            Keep file's materials
          </button>
          <button
            ref={applyRef}
            type="button"
            onClick={() => void apply()}
            disabled={busy}
            className="h-[34px] rounded-lg bg-ed-ink px-4 text-[13px] font-semibold text-white hover:bg-black disabled:opacity-60"
            data-materials-use
          >
            {busy ? "Applying…" : "Apply materials"}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

function PartRow({
  row,
  index,
  busy,
  metalOptions,
  stoneOptions,
  onKind,
  onPreset,
}: {
  row: Row;
  index: number;
  busy: boolean;
  metalOptions: Option[];
  stoneOptions: Option[];
  onKind: (kind: MaterialKind) => void;
  onPreset: (patch: Partial<Answer>) => void;
}) {
  const metal = row.kind === "metal";
  const notes = [
    row.guessed && !row.touched ? "Guessed from its name" : null,
    metal && FINISH_WORDS[row.finish] ? `${FINISH_WORDS[row.finish]} finish` : null,
  ].filter(Boolean);

  return (
    <div
      role="group"
      aria-label={row.name}
      className="flex flex-wrap items-center gap-x-3 gap-y-2 px-5 py-2"
      data-materials-row={row.group.id}
      data-row-index={index}
    >
      <div className="min-w-0 flex-1 basis-[170px]">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-[13px] font-medium text-ed-text" title={row.group.label || undefined}>
            {row.name}
          </span>
          {row.touched ? null : row.matched ? (
            <span
              className="inline-flex shrink-0 items-center gap-0.5 text-[11px] font-semibold text-ed-accent"
              title="Named like a default layer (Metal 01, Gem 01 …), so it gets the default material"
              data-materials-tag="matched"
            >
              <Icon name="check" size={14} />
              Matched
            </span>
          ) : (
            <span
              className="shrink-0 rounded border border-ed-line px-1.5 text-[11px] font-medium leading-4 text-ed-text"
              title="Filled in with our best guess. Check it."
              data-materials-tag="choose"
            >
              Choose
            </span>
          )}
        </div>
        {notes.length ? <p className="mt-0.5 truncate text-[12px] leading-4 text-ed-text-3">{notes.join(" · ")}</p> : null}
      </div>

      <KindSwitch value={row.kind} name={row.name} disabled={busy} onChange={onKind} />
      <PresetSelect
        value={metal ? row.metal : row.stone}
        options={metal ? metalOptions : stoneOptions}
        label={`${row.name}: ${metal ? "which metal" : "which stone"}`}
        disabled={busy}
        onChange={(id) => onPreset(metal ? { metal: id } : { stone: id })}
      />
    </div>
  );
}

/** Metal | Diamond. A radio group: one tab stop, arrow keys switch. */
function KindSwitch({
  value,
  name,
  disabled,
  onChange,
}: {
  value: MaterialKind;
  name: string;
  disabled: boolean;
  onChange: (kind: MaterialKind) => void;
}) {
  const onKey = (e: ReactKeyboardEvent) => {
    if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key)) return;
    e.preventDefault();
    onChange(value === "metal" ? "gem" : "metal");
  };
  return (
    <div role="radiogroup" aria-label={`${name}: metal or diamond`} className="flex h-7 shrink-0 items-center rounded-lg bg-ed-bg p-[2px]">
      {(
        [
          ["metal", "Metal"],
          ["gem", "Diamond"],
        ] as const
      ).map(([kind, word]) => {
        const on = value === kind;
        return (
          <button
            key={kind}
            type="button"
            role="radio"
            aria-checked={on}
            tabIndex={on ? 0 : -1}
            disabled={disabled}
            onClick={() => {
              if (!on) onChange(kind);
            }}
            onKeyDown={onKey}
            className={`h-6 rounded-md px-2.5 text-[12px] font-medium transition-colors ${
              on ? "bg-ed-panel text-ed-text shadow-[0_1px_2px_rgba(0,0,0,0.1)]" : "text-ed-text-2 hover:text-ed-text"
            }`}
            data-kind={kind}
          >
            {word}
          </button>
        );
      })}
    </div>
  );
}

function PresetSelect({
  value,
  options,
  label,
  disabled,
  onChange,
}: {
  value: string;
  options: Option[];
  label: string;
  disabled: boolean;
  onChange: (id: string) => void;
}) {
  // A value the list does not carry (a preset this viewer has but the list
  // hides) is still shown as itself rather than as the first option.
  const shown = options.some((o) => o.id === value) ? options : [...options, { id: value, label: value }];
  return (
    <div className="relative w-[150px] shrink-0">
      <select
        value={value}
        aria-label={label}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="h-7 w-full appearance-none rounded-md border border-ed-line bg-white pl-2 pr-6 text-[12px] text-ed-text outline-none focus:border-ed-accent focus:ring-2 focus:ring-ed-accent/20 disabled:opacity-60"
        data-own-focus
      >
        {shown.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
      <Icon name="keyboard_arrow_down" size={15} className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-ed-text-3" />
    </div>
  );
}
